import React, { useMemo, useState } from "react";
import { Select } from "./Select";
import { buildSubmission } from "./FormSubmission";

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

function FormNotice({ status, message }) {
  if (!status) return null;
  return (
    <div
      className={`form-notice ${status === "success" ? "is-success" : "is-error"}`}
      role="status"
      aria-live="polite"
     style={{ marginTop: "2rem", color: "#EA0029"}} 
    >
      {message}
    </div>
  );
}

export const ConsultationForm = ({ onSubmitData }) => {
  const [projectType, setProjectType] = useState("");
  const [foundUs, setFoundUs] = useState("");

  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [projectDetails, setProjectDetails] = useState("");

  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState({ state: "idle", message: "" }); // idle | loading | success | error

  const pickValue = (arg) => (arg && typeof arg === "object" ? arg.value : arg) ?? "";

  const requiredKeys = ["name", "company", "email", "projectDetails"];

  const submission = useMemo(
    () =>
      buildSubmission({
        kind: "consultation",
        fields: { projectType, foundUs, name, company, email, phone, projectDetails },
        meta: { page: window.location.pathname },
      }),
    [projectType, foundUs, name, company, email, phone, projectDetails]
  );

  const baseValidation = submission.validate(requiredKeys); // uses your existing helper :contentReference[oaicite:7]{index=7}
  const emailOk = email ? isEmail(email) : false;

  const errors = {
    name: baseValidation.missing.includes("name") ? "" : "",
    company: baseValidation.missing.includes("company") ? "" : "",
    email:
      baseValidation.missing.includes("email")
        ? ""
        : !emailOk
        ? ""
        : "",
    projectDetails: baseValidation.missing.includes("projectDetails") ? "" : "",
  };

  const canSubmit =
    status.state !== "loading" &&
    baseValidation.ok &&
    emailOk;

  const showInvalidEmailNotice = !!email && !emailOk;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, company: true, email: true, projectDetails: true });

    if (!canSubmit) {
      setStatus({ state: "error", message: "" });
      return;
    }

    try {
      setStatus({ state: "loading", message: "" });
      if (typeof onSubmitData === "function") await onSubmitData(submission);

      setStatus({ state: "success", message: "" });
      setTimeout(() => setStatus({ state: "idle", message: "" }), 2000);

      // clear after success
      setProjectType("");
      setFoundUs("");
      setName("");
      setCompany("");
      setEmail("");
      setPhone("");
      setProjectDetails("");
      setTouched({});
    } catch (err) {
      setStatus({ state: "error", message: err?.message || "Something went wrong. Please try again." });
    }
  };

  const fieldClass = (key) => (touched[key] && errors[key] ? "has-error" : "");

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="slope-contact-form">
        <div className="select-wrapper">
          <div>
            <label>Select project type</label>
            <span className="source-select">
              <Select
                options={[
                  { value: "", label: "Select an option" },
                  { value: "any", label: "Any" },
                  { value: "virtual-reality", label: "Virtual Reality" },
                  { value: "augmented-reality", label: "Augmented Reality" },
                  { value: "app-software", label: "App & Software" },
                  { value: "installation", label: "Installation" },
                  { value: "simulation", label: "Simulation" },
                ]}
                value={projectType}
                onChange={(arg) => setProjectType(pickValue(arg))}
              />
            </span>
          </div>

          <div>
            <label>How did you find us?</label>
            <span className="source-select">
              <Select
                options={[
                  { value: "", label: "Select an option" },
                  { value: "google", label: "Google" },
                  { value: "facebook", label: "Facebook" },
                  { value: "other", label: "Other" },
                ]}
                value={foundUs}
                onChange={(arg) => setFoundUs(pickValue(arg))}
              />
            </span>
          </div>
        </div>

        <div className="fields-wrapper">
          <div className="input-contact">
            <span className={`name-field ${fieldClass("name")}`}>
              <input
                value={name}
                onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                onChange={(e) => setName(e.target.value)}
                type="text"
                placeholder="Your name *"
                aria-invalid={!!(touched.name && errors.name)}
              />
              {touched.name && errors.name ? <small className="field-error">{errors.name}</small> : null}
            </span>

            <span className={`company-field ${fieldClass("company")}`}>
              <input
                value={company}
                onBlur={() => setTouched((t) => ({ ...t, company: true }))}
                onChange={(e) => setCompany(e.target.value)}
                type="text"
                placeholder="Your company *"
                aria-invalid={!!(touched.company && errors.company)}
              />
              {touched.company && errors.company ? <small className="field-error">{errors.company}</small> : null}
            </span>

            <span className={`email-field ${fieldClass("email")}`}>
              <input
                value={email}
                onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                placeholder="Email address *"
                aria-invalid={!!(touched.email && errors.email)}
              />
              {touched.email && errors.email ? <small className="field-error">{errors.email}</small> : null}
            </span>

            <span className="number-field">
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                type="tel"
                placeholder="Phone"
              />
            </span>
          </div>

          <div className="textarea-contact">
            <span className={`message-field ${fieldClass("projectDetails")}`}>
              <textarea
                value={projectDetails}
                onBlur={() => setTouched((t) => ({ ...t, projectDetails: true }))}
                onChange={(e) => setProjectDetails(e.target.value)}
                placeholder="Tell us about the project *"
                aria-invalid={!!(touched.projectDetails && errors.projectDetails)}
              />
              {touched.projectDetails && errors.projectDetails ? (
                <small className="field-error">{errors.projectDetails}</small>
              ) : null}
            </span>
          </div>
        </div>

        <div className="submit-contact">
          <input
            type="submit"
            value={status.state === "loading" ? "Sending..." : status.state === "success" ? "Sent ✓" : "Submit"}
            disabled={!canSubmit}
            style={{ cursor: canSubmit ? "pointer" : "not-allowed" }}
          />
          {showInvalidEmailNotice ? (
            <span style={{ marginLeft: "1rem", color: "#EA0029" }}>Email is invalid.</span>
          ) : null}
          <FormNotice status={status.state === "success" ? "success" : status.state === "error" ? "error" : ""} message={status.message} />
        </div>
      </div>
    </form>
  );
};
